import type { RoadmapGenerationDraft } from '@topolo/domain';
import type {
  DeckExportJob,
  DeckTemplate,
  PresentationDeck,
  SlideSpec,
} from '@topolo/domain';
import { getRoadmapperApiBaseUrl } from './serviceUrls';
import { RoadmapperApiClientBase } from './roadmapper-api-client-base';
import { queryClient, getWorkspaceScope } from './queryClient';
import { tokenStorage } from './auth';
import { buildUserFromAccessToken } from './secure-auth';
export type {
  GuestAccessMode,
  GuestInviteCapabilities,
  GuestShareRecord,
} from './roadmapper-api-client-base';

// Resolve API base URL
// In dev, always target local worker unless you explicitly change this line
const API_BASE = getRoadmapperApiBaseUrl();


// Web-facing API response shapes.
// The API serializes domain entities to JSON, so Date fields arrive as ISO
// strings. These mirror the domain entities (packages/domain) with string
// dates plus the computed fields the list/detail endpoints add.
export type ProjectStatus =
  | 'active'
  | 'on_hold'
  | 'completed'
  | 'cancelled'
  | 'archived';

export interface ApiProject {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  createdAt: string;
  updatedAt: string;
  roadmapsCount?: number;
  itemsCount?: number;
  progress?: number;
  collaboration?: ApiResourceCollaboration;
}

// Execution/progress status derived from a roadmap's items (server-side).
export type RoadmapProgressStatus =
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'blocked';

export interface ApiRoadmap {
  weight?: number;
  id: string;
  projectId: string;
  organizationId: string;
  name: string;
  description?: string;
  repository?: string;
  startDate?: string;
  endDate?: string;
  // The list/detail endpoints always attach a derived lifecycle status and a
  // progressStatus via attachRoadmapProgressStatus (see api roadmaps route).
  status: 'active' | 'completed' | 'archived';
  progressStatus?: RoadmapProgressStatus;
  createdAt: string;
  updatedAt: string;
  collaboration?: ApiRoadmapCollaboration;
}

export interface ApiResourceCollaboration {
  grantId: string;
  scope: 'read' | 'read_write' | 'admin';
  resourceType?: 'project' | 'roadmap';
  resourceId?: string;
  projectId?: string;
  roadmapId?: string;
  sourceOrganizationId?: string;
  sourceWorkspaceId?: string;
  sourceOrganizationName?: string;
  organizationId?: string;
  organizationName?: string;
  projectName?: string;
}

export type ApiRoadmapCollaboration = ApiResourceCollaboration;

export interface ApiSharedProject extends ApiProject {
  collaboration: ApiResourceCollaboration;
}

export interface ApiSharedRoadmap extends ApiRoadmap {
  projectName: string;
  collaboration: ApiRoadmapCollaboration;
}

export interface ApiResourceCollaborator {
  id: string;
  userId: string;
  scope: 'read' | 'read_write' | 'admin';
  createdAt: number;
  expiresAt?: number | null;
  recipient?: { id: string; email: string; name?: string | null } | null;
}

export type ApiRoadmapCollaborator = ApiResourceCollaborator;

export type RoadmapItemStatus =
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'blocked';

export interface DashboardInput {
  section?: 'work' | 'progress' | 'edits';
  state?: 'all' | 'active' | 'blocked' | 'completed';
  projectId?: string; search?: string; since?: string; cursor?: string; limit?: number;
}
export interface DashboardIdentity { userId: string; organizationId: string; workspaceId: string }
export interface DashboardRow {
  id: string; kind: 'work' | 'transition' | 'created' | 'edited' | 'dependency_added';
  itemId: string; itemName: string; roadmapId: string; roadmapName: string; projectId: string; projectName: string;
  status: RoadmapItemStatus; oldStatus: RoadmapItemStatus | null; occurredAt: string;
  actorId: string | null; source: string | null; eventKind: string | null; assignee: string | null;
  isBlocked: boolean; blockerCount: number;
  blocker: { itemId: string; itemName: string; href: string } | null; href: string;
}
export interface ApiDashboard {
  snapshotAt: string; scope: { organizationId: string; workspaceId: string };
  section: 'work' | 'progress' | 'edits';
  filters: { state: 'all' | 'active' | 'blocked' | 'completed'; projectId: string | null; search: string; since: string | null };
  rows: DashboardRow[]; nextCursor: string | null; hasMore: boolean;
  coverage: { complete: boolean; partial: boolean; errors: string[]; historyRecordedFrom: string };
}

export type RoadmapItemPriority = 'low' | 'medium' | 'high' | 'critical';
export type RoadmapAssigneeType = 'human' | 'topolo_agent' | 'ai_runtime';
export type RoadmapAutonomyLevel = 'supervised' | 'guardrailed' | 'autonomous';
export type AgenticComputeProfile = 'balanced' | 'high' | 'burst';

export interface ApiRoadmapItem {
  id: string;
  roadmapId: string;
  organizationId: string;
  name: string;
  description?: string;
  status: RoadmapItemStatus;
  priority: RoadmapItemPriority;
  startDate?: string;
  endDate?: string;
  parentItemId?: string;
  assignee?: string;
  assigneeType?: RoadmapAssigneeType;
  assigneeId?: string;
  autonomyLevel?: RoadmapAutonomyLevel;
  estimatedEffortHours?: number;
  tokenBudget?: number;
  /** How much this item counts among its siblings when progress rolls up. */
  weight?: number;
  /** Where this leaf actually is, 0-100. Ignored once the item has children. */
  progressPercent?: number;
  /** How far this item is once everything beneath it is counted, 0-100. */
  progress?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAgenticForecastItem {
  id: string;
  name: string;
  status: RoadmapItemStatus;
  priority: RoadmapItemPriority;
  assignee?: string;
  assigneeType?: RoadmapAssigneeType;
  assigneeId?: string;
  autonomyLevel?: RoadmapAutonomyLevel;
  predecessorIds: string[];
  forecastStart: string;
  forecastEnd: string;
  durationHours: number;
  estimatedEffortHours: number;
  estimateSource: 'explicit' | 'default';
  tokenBudget: number;
  source: 'constraint' | 'computed';
  readiness: 'complete' | 'blocked' | 'ready' | 'waiting';
  confidence: number;
  criticalPath: boolean;
  cycleDetected: boolean;
}

export interface ApiAgenticExecutionForecast {
  profile: AgenticComputeProfile;
  generatedAt: string;
  maxParallelism: number;
  speedMultiplier: number;
  forecastStart: string;
  forecastEnd: string;
  wallClockHours: number;
  totalAgentHours: number;
  totalTokenBudget: number;
  criticalPathHours: number;
  readyCount: number;
  blockedCount: number;
  waitingCount: number;
  completedCount: number;
  constrainedCount: number;
  computedCount: number;
  items: ApiAgenticForecastItem[];
}

export interface ApiRoadmapItemDependency {
  id: string;
  fromItemId: string;
  toItemId: string;
  type: 'blocks' | 'requires' | 'related';
  organizationId: string;
  createdAt: string;
}

export interface ApiComment {
  id: string;
  roadmapItemId: string;
  organizationId: string;
  userId: string;
  userName: string;
  userEmail: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAttachment {
  id: string;
  roadmapItemId: string;
  organizationId: string;
  userId: string;
  userName: string;
  userEmail: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  r2Key: string;
  createdAt: string;
}

export interface ArchiveProjectResult {
  success: boolean;
  projectId: string;
  status: 'archived' | 'active';
  roadmapsAffected: number;
}

export interface CreatedRecord {
  id: string;
}

export interface ImportResult {
  meta?: {
    parsed?: number;
    skipped?: number;
    [key: string]: unknown;
  };
  warnings?: string[];
  created?: {
    projects?: number;
    roadmaps?: number;
    roadmapItems?: number;
  };
  entities?: {
    projects?: Array<{ name: string }>;
    roadmaps?: Array<{ name: string }>;
    roadmapItems?: Array<{ name: string }>;
  };
  [key: string]: unknown;
}

// The import endpoint may return the result at the top level, nested under
// `result`, or under `data.result` depending on the path, so ImportResponse
// extends ImportResult to keep the defensive fallback access type-safe.
export interface ImportResponse extends ImportResult {
  format?: string;
  result?: ImportResult;
  data?: { result?: ImportResult };
}

// Report shapes mirror packages/domain generate-report.ts (GeneratedReport /
// ReportMetrics) but with date fields as the ISO strings the API serializes.
export interface ReportMetrics {
  totalProjects: number;
  totalRoadmaps: number;
  totalItems: number;
  completedItems: number;
  inProgressItems: number;
  plannedItems: number;
  blockedItems: number;
  workItems?: number;
  overallProgress: number;
  statusDistribution: {
    planned: number;
    in_progress: number;
    completed: number;
    blocked: number;
  };
  priorityDistribution: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  upcomingItems: Array<{
    itemId: string;
    itemName: string;
    projectName: string;
    roadmapName: string;
    startDate?: string;
    endDate?: string;
    status: string;
    priority: string;
    assignee?: string;
  }>;
  overdueItems: Array<{
    itemId: string;
    itemName: string;
    projectId: string;
    projectName: string;
    roadmapId: string;
    roadmapName: string;
    startDate?: string;
    endDate?: string;
    status: string;
    priority: string;
    assignee?: string;
    daysOverdue: number;
  }>;
  projectBreakdown: Array<{
    projectId: string;
    projectName: string;
    totalRoadmaps: number;
    totalItems: number;
    completedItems: number;
    inProgressItems: number;
    plannedItems: number;
    blockedItems: number;
    progress: number;
    startDate?: string;
    endDate?: string;
  }>;
  roadmapBreakdown: Array<{
    roadmapId: string;
    roadmapName: string;
    projectName: string;
    totalItems: number;
    completedItems: number;
    inProgressItems: number;
    plannedItems: number;
    blockedItems: number;
    progress: number;
    startDate?: string;
    endDate?: string;
  }>;
  velocity: {
    itemsCompletedLast30Days: number;
    itemsCompletedLast7Days: number;
    averageItemsPerWeek: number;
    projectedCompletionDate?: string;
  };
  health: {
    score: number;
    factors: {
      onTimeDelivery: number;
      overdueRatio: number;
      blockedRatio: number;
      velocityTrend: 'increasing' | 'decreasing' | 'stable';
    };
  };
}

export interface GeneratedReport {
  id: string;
  organizationId: string;
  title: string;
  description?: string;
  filters: unknown;
  metrics: ReportMetrics;
  generatedAt: string;
  generatedBy: string;
}

export interface GenerateReportResponse {
  report: GeneratedReport;
}

export interface ApiPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface ApiPage<T> {
  items: T[];
  pagination: ApiPagination;
}

export interface PageParams {
  limit?: number;
  offset?: number;
}

// One HTTP request per page; the auto-paging helpers below are bounded by both
// of these so a mis-reporting API can never spin the client forever.
const PAGE_SIZE = 200; // the API clamps limit to [1, 200]
const MAX_PAGES = 50;
const MAX_ROWS = 5000;

// Tolerates an API that has not yet shipped pagination (the web deploys
// independently of the api worker): a body with no `pagination` object is one
// complete page.
function normalizePage<T>(body: unknown): ApiPage<T> {
  const record = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const rawItems = Array.isArray(body) ? body : record.data;
  const items = (Array.isArray(rawItems) ? rawItems : []) as T[];
  const raw = record.pagination;
  if (!raw || typeof raw !== 'object') {
    return {
      items,
      pagination: { limit: items.length, offset: 0, total: items.length, hasMore: false },
    };
  }
  const p = raw as Record<string, unknown>;
  return {
    items,
    pagination: {
      limit: typeof p.limit === 'number' ? p.limit : items.length,
      offset: typeof p.offset === 'number' ? p.offset : 0,
      total: typeof p.total === 'number' ? p.total : items.length,
      hasMore: p.hasMore === true,
    },
  };
}

/**
 * One call returning a whole project's roadmaps, every item across them, and
 * every dependency inside the project. Replaces a fan-out of one unbounded
 * request per roadmap, and is the only source that returns dependencies which
 * cross roadmap boundaries — the per-roadmap endpoint filters those out.
 */
export interface ApiProjectPlanningItem {
  id: string;
  kind: 'milestone' | 'item';
  roadmapId: string;
  roadmapName: string;
  parentItemId: string | null;
  name: string;
  description: string | null;
  status: 'planned' | 'in_progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'critical';
  /** How much this item counts among its siblings when progress rolls up. */
  weight?: number;
  /** An explicit figure for a leaf, 0-100, if one was set. */
  progressPercent?: number;
  /** How far this item is once everything beneath it is counted, 0-100. */
  progress?: number;
  startDate: string | null;
  endDate: string | null;
}

export interface ApiProjectPlanningDependency {
  id: string;
  type: 'blocks' | 'requires' | 'related';
  fromItemId: string;
  fromRoadmapId: string;
  toItemId: string;
  toRoadmapId: string;
}

export interface ApiProjectPlanningFeed {
  items: ApiProjectPlanningItem[];
  dependencies: ApiProjectPlanningDependency[];
}

class ApiClient extends RoadmapperApiClientBase {

  // Reads the FULL response body (data + pagination) without changing
  // handleResponse's unwrapping behaviour for every other caller: the shim
  // re-wraps the body so handleResponse's `data` unwrap yields the whole
  // envelope, while its error/401 handling is reused as-is.
  private async handlePagedResponse<T>(response: Response): Promise<ApiPage<T>> {
    const shim = {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      text: () => response.text(),
      json: async () => ({ data: await response.json() }),
    } as unknown as Response;
    const body = await this.handleResponse<unknown>(shim);
    return normalizePage<T>(body);
  }

  private async collectPages<T>(
    fetchPage: (params: Required<PageParams>) => Promise<ApiPage<T>>,
    label: string,
  ): Promise<T[]> {
    const items: T[] = [];
    let offset = 0;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const result = await fetchPage({ limit: PAGE_SIZE, offset });
      items.push(...result.items);
      offset += result.items.length > 0 ? result.items.length : PAGE_SIZE;
      if (!result.pagination.hasMore || items.length >= MAX_ROWS) {
        if (result.pagination.hasMore) {
          console.warn(
            `[api] ${label}: stopped auto-paging at ${items.length} rows (safety cap); results are truncated.`,
          );
        }
        return items;
      }
    }
    console.warn(
      `[api] ${label}: stopped auto-paging after ${MAX_PAGES} pages (safety cap); results are truncated.`,
    );
    return items;
  }

  async getDashboard(input: DashboardInput = {}, signal?: AbortSignal, expected?: DashboardIdentity): Promise<ApiDashboard> {
    const tokens = tokenStorage.getTokens();
    const initialUser = tokens?.accessToken ? buildUserFromAccessToken(tokens.accessToken) : null;
    const workspace = getWorkspaceScope();
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(input)) if (value !== undefined && value !== '') search.set(key, String(value));
    const response = await this.createAuthenticatedFetch(true)(`${API_BASE}/dashboard?${search}`, { signal });
    const data = await this.handleResponse<ApiDashboard>(response);
    const currentTokens = tokenStorage.getTokens();
    const currentUser = currentTokens?.accessToken ? buildUserFromAccessToken(currentTokens.accessToken) : null;
    if (initialUser?.id !== currentUser?.id || initialUser?.orgId !== currentUser?.orgId || workspace !== getWorkspaceScope()
      || (expected && (initialUser?.id !== expected.userId || initialUser?.orgId !== expected.organizationId
        || data.scope.organizationId !== expected.organizationId
        || (expected.workspaceId !== 'default' && data.scope.workspaceId !== expected.workspaceId)))) {
      throw new Error('dashboard_scope_changed');
    }
    return data;
  }

  private async handleDashboardMutationResponse<T>(response: Response): Promise<T> {
    const result = await this.handleResponse<T>(response);
    void queryClient.invalidateQueries({ queryKey: ['work-dashboard'] });
    return result;
  }

  // Projects API
  async getProjectsPage(params?: PageParams, liveOnly = false, signal?: AbortSignal): Promise<ApiPage<ApiProject>> {
    const fetchAuth = this.createAuthenticatedFetch(liveOnly);
    const search = new URLSearchParams();
    search.set('limit', String(params?.limit ?? PAGE_SIZE));
    search.set('offset', String(params?.offset ?? 0));
    const response = await fetchAuth(`${API_BASE}/projects?${search.toString()}`, { signal });
    return this.handlePagedResponse<ApiProject>(response);
  }

  // Returns the COMPLETE collection by walking pages until hasMore is false.
  async getProjects(): Promise<ApiProject[]> {
    return this.collectPages<ApiProject>((p) => this.getProjectsPage(p), 'getProjects');
  }

  async getProject(id: string): Promise<ApiProject> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${id}`);
    return this.handleResponse(response);
  }

  async getProjectDashboard<T = unknown>(id: string): Promise<T> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${id}/dashboard`);
    return this.handleResponse<T>(response);
  }

  async createProjectFromPlan(data: {
    plan: RoadmapGenerationDraft;
  }): Promise<{ projectId: string; created: { roadmaps: number; items: number } }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/from-plan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async generateProjectPresentation(
    projectId: string,
    data?: {
      template?: DeckTemplate;
      scenarioId?: string;
      liveLinked?: boolean;
    },
  ): Promise<PresentationDeck> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${projectId}/presentations/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data || {}),
    });
    return this.handleResponse(response);
  }

  async generateRoadmapPresentation(
    roadmapId: string,
    data?: {
      template?: DeckTemplate;
      liveLinked?: boolean;
    },
  ): Promise<PresentationDeck> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${roadmapId}/presentations/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data || {}),
    });
    return this.handleResponse(response);
  }

  async generateRoadmapItemPresentation(
    itemId: string,
    data?: {
      template?: DeckTemplate;
      liveLinked?: boolean;
    },
  ): Promise<PresentationDeck> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/${itemId}/presentations/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data || {}),
    });
    return this.handleResponse(response);
  }

  async getPresentation(presentationId: string): Promise<PresentationDeck> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/presentations/${presentationId}`);
    return this.handleResponse(response);
  }

  async updatePresentation(
    presentationId: string,
    data: {
      title?: string;
      summary?: string;
      slides?: SlideSpec[];
    },
  ): Promise<PresentationDeck> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/presentations/${presentationId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async exportPresentation(
    presentationId: string,
    data: { format: 'web' | 'pptx' },
  ): Promise<{ job: DeckExportJob; presentation: PresentationDeck }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/presentations/${presentationId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async createProject(project: {
    name: string;
    description?: string;
    status?: 'active' | 'on_hold' | 'completed' | 'cancelled' | 'archived';
  }): Promise<ApiProject> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(project),
    });
    return this.handleResponse(response);
  }

  async updateProject(
    id: string,
    project: Partial<{
      name: string;
      description?: string;
      status?: 'active' | 'on_hold' | 'completed' | 'cancelled' | 'archived';
    }>,
  ): Promise<ApiProject> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(project),
    });
    return this.handleResponse(response);
  }

  async deleteProject(id: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${id}`, {
      method: 'DELETE',
    });
    return this.handleDashboardMutationResponse(response);
  }

  async archiveProject(id: string, archive: boolean): Promise<ArchiveProjectResult> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${id}/archive`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ archive }),
    });
    return this.handleResponse(response);
  }

  // Roadmaps API
  async getRoadmapsPage(
    params?: PageParams & { projectId?: string; startDate?: string; endDate?: string },
  ): Promise<ApiPage<ApiRoadmap>> {
    const fetchAuth = this.createAuthenticatedFetch();
    const search = new URLSearchParams();
    if (params?.projectId) search.set('projectId', params.projectId);
    if (params?.startDate) search.set('startDate', params.startDate);
    if (params?.endDate) search.set('endDate', params.endDate);
    search.set('limit', String(params?.limit ?? PAGE_SIZE));
    search.set('offset', String(params?.offset ?? 0));

    const response = await fetchAuth(`${API_BASE}/roadmaps?${search.toString()}`);
    return this.handlePagedResponse<ApiRoadmap>(response);
  }

  // Returns the COMPLETE collection by walking pages until hasMore is false.
  async getRoadmaps(projectId?: string, startDate?: string, endDate?: string): Promise<ApiRoadmap[]> {
    return this.collectPages<ApiRoadmap>(
      (p) => this.getRoadmapsPage({ ...p, projectId, startDate, endDate }),
      'getRoadmaps',
    );
  }

  async getRoadmap(id: string): Promise<ApiRoadmap> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${id}`);
    return this.handleResponse(response);
  }

  async getSharedRoadmaps(): Promise<ApiSharedRoadmap[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/collaboration/roadmaps`);
    const data = await this.handleResponse<{ roadmaps: ApiSharedRoadmap[] }>(response);
    return data.roadmaps;
  }

  async getSharedProjects(): Promise<ApiSharedProject[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/collaboration/projects`);
    const data = await this.handleResponse<{ projects: ApiSharedProject[] }>(response);
    return data.projects;
  }

  async getResourceCollaborators(resourceType: 'project' | 'roadmap', id: string): Promise<ApiResourceCollaborator[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/${resourceType}s/${id}/collaborators`);
    const data = await this.handleResponse<{ grants: ApiResourceCollaborator[] }>(response);
    return data.grants;
  }

  async createResourceCollaborator(
    resourceType: 'project' | 'roadmap',
    id: string,
    input: { email: string; access: 'viewer' | 'editor' },
  ): Promise<{ grant: ApiResourceCollaborator; provisioned: boolean }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/${resourceType}s/${id}/collaborators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return this.handleResponse(response);
  }

  async updateResourceCollaborator(
    resourceType: 'project' | 'roadmap',
    id: string,
    grantId: string,
    access: 'viewer' | 'editor',
  ): Promise<{ grant: ApiResourceCollaborator }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/${resourceType}s/${id}/collaborators/${grantId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access }),
    });
    return this.handleResponse(response);
  }

  async revokeResourceCollaborator(resourceType: 'project' | 'roadmap', id: string, grantId: string): Promise<void> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/${resourceType}s/${id}/collaborators/${grantId}`, {
      method: 'DELETE',
    });
    await this.handleResponse(response);
  }

  async getRoadmapCollaborators(id: string): Promise<ApiRoadmapCollaborator[]> {
    return this.getResourceCollaborators('roadmap', id);
  }

  async createRoadmapCollaborator(
    id: string,
    input: { email: string; access: 'viewer' | 'editor' },
  ): Promise<{ grant: ApiRoadmapCollaborator; provisioned: boolean }> {
    return this.createResourceCollaborator('roadmap', id, input);
  }

  async updateRoadmapCollaborator(
    id: string,
    grantId: string,
    access: 'viewer' | 'editor',
  ): Promise<{ grant: ApiRoadmapCollaborator }> {
    return this.updateResourceCollaborator('roadmap', id, grantId, access);
  }

  async revokeRoadmapCollaborator(id: string, grantId: string): Promise<void> {
    await this.revokeResourceCollaborator('roadmap', id, grantId);
  }

  async createRoadmap(roadmap: {
    projectId: string;
    name: string;
    description?: string;
    repository?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiRoadmap> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(roadmap),
    });
    return this.handleResponse(response);
  }

  async updateRoadmap(
    id: string,
    roadmap: Partial<{
      weight?: number;
      name: string;
      description?: string | null;
      repository?: string | null;
      projectId?: string;
      startDate?: string | null;
      endDate?: string | null;
      status?: 'active' | 'completed' | 'archived';
    }>,
  ): Promise<ApiRoadmap> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(roadmap),
    });
    return this.handleDashboardMutationResponse(response);
  }

  async deleteRoadmap(id: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${id}`, {
      method: 'DELETE',
    });
    return this.handleDashboardMutationResponse(response);
  }

  // Roadmap Items API
  async getProjectPlanningFeed(projectId: string): Promise<ApiProjectPlanningFeed> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/projects/${projectId}/planning-feed`);
    const feed = await this.handleResponse<ApiProjectPlanningFeed>(response);
    return {
      items: Array.isArray(feed?.items) ? feed.items : [],
      dependencies: Array.isArray(feed?.dependencies) ? feed.dependencies : [],
    };
  }

  async getRoadmapItems(roadmapId: string): Promise<ApiRoadmapItem[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${roadmapId}/items`);
    return this.handleResponse(response);
  }

  async getRoadmapExecutionForecast(
    roadmapId: string,
    profile: AgenticComputeProfile,
  ): Promise<ApiAgenticExecutionForecast> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(
      `${API_BASE}/roadmaps/${roadmapId}/execution-forecast?profile=${encodeURIComponent(profile)}`,
    );
    return this.handleResponse(response);
  }

  async getRoadmapDependencies(roadmapId: string): Promise<ApiRoadmapItemDependency[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/roadmaps/${roadmapId}/dependencies`);
    return this.handleResponse(response);
  }

  /**
   * Move an item, and everything beneath it, into another roadmap.
   * `updateRoadmapItem` cannot: an item's roadmap is fixed at creation.
   */
  async moveRoadmapItemToRoadmap(itemId: string, roadmapId: string): Promise<{ movedItemIds: string[] }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/${itemId}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roadmapId }),
    });
    return this.handleDashboardMutationResponse(response);
  }

  async getRoadmapItem(itemId: string): Promise<ApiRoadmapItem> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/${itemId}`);
    return this.handleResponse(response);
  }

  async updateRoadmapItem(
    itemId: string,
    data: {
      name?: string;
      description?: string;
      status?: 'planned' | 'in_progress' | 'completed' | 'blocked';
      priority?: 'low' | 'medium' | 'high' | 'critical';
      startDate?: Date | null;
      endDate?: Date | null;
      assignee?: string | null;
      assigneeType?: RoadmapAssigneeType | null;
      assigneeId?: string | null;
      autonomyLevel?: RoadmapAutonomyLevel | null;
      estimatedEffortHours?: number | null;
      tokenBudget?: number | null;
      weight?: number | null;
      progressPercent?: number | null;
      parentItemId?: string | null;
    },
  ): Promise<ApiRoadmapItem> {
    const fetchAuth = this.createAuthenticatedFetch();

    // Convert dates to ISO strings for API
    const requestData = {
      ...data,
      startDate: data.startDate instanceof Date ? data.startDate.toISOString() : data.startDate,
      endDate: data.endDate instanceof Date ? data.endDate.toISOString() : data.endDate,
    };

    const response = await fetchAuth(`${API_BASE}/items/${itemId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    return this.handleDashboardMutationResponse(response);
  }

  async createRoadmapItem(data: {
    roadmapId: string;
    name: string;
    description?: string;
    status: 'planned' | 'in_progress' | 'completed' | 'blocked';
    priority: 'low' | 'medium' | 'high' | 'critical';
    startDate?: Date;
    endDate?: Date;
    assignee?: string;
    assigneeType?: RoadmapAssigneeType;
    assigneeId?: string;
    autonomyLevel?: RoadmapAutonomyLevel;
    estimatedEffortHours?: number;
    tokenBudget?: number;
    parentItemId?: string; // For creating subitems
  }): Promise<ApiRoadmapItem> {
    const fetchAuth = this.createAuthenticatedFetch();

    // Convert dates to ISO strings for API
    const requestData = {
      ...data,
      startDate: data.startDate?.toISOString(),
      endDate: data.endDate?.toISOString(),
    };

    const response = await fetchAuth(`${API_BASE}/items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    return this.handleDashboardMutationResponse(response);
  }

  async createSubitem(parentItemId: string, data: {
    roadmapId: string;
    name: string;
    description?: string;
    status: 'planned' | 'in_progress' | 'completed' | 'blocked';
    priority: 'low' | 'medium' | 'high' | 'critical';
    startDate?: Date;
    endDate?: Date;
    assignee?: string;
    assigneeType?: RoadmapAssigneeType;
    assigneeId?: string;
    autonomyLevel?: RoadmapAutonomyLevel;
    estimatedEffortHours?: number;
    tokenBudget?: number;
  }) {
    return this.createRoadmapItem({
      ...data,
      parentItemId,
    });
  }

  async deleteRoadmapItem(itemId: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/${itemId}`, {
      method: 'DELETE',
    });
    return this.handleDashboardMutationResponse(response);
  }

  // Dependencies API
  async createDependency(data: {
    fromItemId: string;
    toItemId: string;
    type: 'blocks' | 'requires' | 'related';
  }): Promise<ApiRoadmapItemDependency> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/dependencies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleDashboardMutationResponse(response);
  }

  async getDependencies(itemId: string): Promise<ApiRoadmapItemDependency[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/dependencies/item/${itemId}`);
    return this.handleResponse(response);
  }

  async deleteDependency(dependencyId: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/dependencies/${dependencyId}`, {
      method: 'DELETE',
    });
    return this.handleDashboardMutationResponse(response);
  }

  // Import API
  async importRoadmap(file: File): Promise<ImportResponse> {
    const fetchAuth = this.createAuthenticatedFetch();
    const form = new FormData();
    form.append('file', file);
    const response = await fetchAuth(`${API_BASE}/import`, {
      method: 'POST',
      body: form,
    });
    return this.handleResponse(response);
  }

  async importData(file: File, importType: 'roadmaps' | 'projects' | 'combined'): Promise<ImportResponse> {
    const fetchAuth = this.createAuthenticatedFetch();
    const form = new FormData();
    form.append('file', file);
    form.append('importType', importType);
    const response = await fetchAuth(`${API_BASE}/import`, {
      method: 'POST',
      body: form,
    });
    return this.handleResponse(response);
  }

  async getRoadmapByGuestToken(token: string): Promise<unknown> {
    // No authentication required for guest access
    const response = await fetch(`${API_BASE}/roadmaps/guest/${token}`);
    return this.handleResponse(response);
  }

  async getProjectByGuestToken(token: string): Promise<unknown> {
    const response = await fetch(`${API_BASE}/projects/guest/${token}`);
    return this.handleResponse(response);
  }

  async getRoadmapItemByGuestToken(token: string): Promise<unknown> {
    const response = await fetch(`${API_BASE}/items/guest/${token}`);
    return this.handleResponse(response);
  }

  async createGuestScopedRoadmapItem(
    token: string,
    data: {
      roadmapId: string;
      name: string;
      description?: string;
      status: 'planned' | 'in_progress' | 'completed' | 'blocked';
      priority: 'low' | 'medium' | 'high' | 'critical';
      startDate?: string;
      endDate?: string;
      assignee?: string;
      parentItemId?: string;
    },
  ): Promise<ApiRoadmapItem> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/guest/${token}/items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async updateGuestScopedRoadmapItem(
    token: string,
    itemId: string,
    data: {
      name?: string;
      description?: string;
      status?: 'planned' | 'in_progress' | 'completed' | 'blocked';
      priority?: 'low' | 'medium' | 'high' | 'critical';
      startDate?: string;
      endDate?: string;
      assignee?: string;
      parentItemId?: string;
    },
  ): Promise<ApiRoadmapItem> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/items/guest/${token}/items/${itemId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  // Comments API
  async getComments(roadmapItemId: string): Promise<ApiComment[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/comments/roadmap-item/${roadmapItemId}`);
    return this.handleResponse(response);
  }

  async createComment(roadmapItemId: string, content: string): Promise<ApiComment> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ roadmapItemId, content }),
    });
    return this.handleResponse(response);
  }

  async updateComment(commentId: string, content: string): Promise<ApiComment> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/comments/${commentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
    });
    return this.handleResponse(response);
  }

  async deleteComment(commentId: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/comments/${commentId}`, {
      method: 'DELETE',
    });
    return this.handleResponse(response);
  }

  // Attachments API
  async getAttachments(roadmapItemId: string, opts?: { includeDescendants?: boolean }): Promise<ApiAttachment[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const query = opts?.includeDescendants ? '?includeDescendants=true' : '';
    const response = await fetchAuth(`${API_BASE}/attachments/roadmap-item/${roadmapItemId}${query}`);
    return this.handleResponse(response);
  }

  async getRoadmapAttachments(roadmapId: string): Promise<ApiAttachment[]> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/attachments/roadmap/${roadmapId}`);
    return this.handleResponse(response);
  }

  async uploadAttachment(roadmapItemId: string, file: File): Promise<ApiAttachment> {
    const fetchAuth = this.createAuthenticatedFetch();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('roadmapItemId', roadmapItemId);

    const response = await fetchAuth(`${API_BASE}/attachments/upload`, {
      method: 'POST',
      body: formData,
    });
    return this.handleResponse(response);
  }

  async downloadAttachment(attachmentId: string) {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/attachments/${attachmentId}/download`);
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    
    return response.arrayBuffer();
  }

  async deleteAttachment(attachmentId: string): Promise<{ id: string }> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
    return this.handleResponse(response);
  }

  // Reports API
  async generateReport(reportData: {
    title: string;
    description?: string;
    filters: {
      projectIds?: string[];
      roadmapIds?: string[];
      statuses?: string[];
      priorities?: string[];
      dateRange?: {
        start: string;
        end: string;
      };
      assignees?: string[];
    };
  }): Promise<GenerateReportResponse> {
    const fetchAuth = this.createAuthenticatedFetch();
    const response = await fetchAuth(`${API_BASE}/reports/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reportData),
    });
    return this.handleResponse(response);
  }

  /**
   * Ask Roadmapper to archive a workspace.
   *
   * The only workspace lifecycle call this client still makes. Listing,
   * creating, renaming and setting the default go straight to the platform from
   * the app-shell's `useAppWorkspace` hook — this app has no workspace registry
   * left to talk to. Archival routes through Roadmapper because only Roadmapper
   * can say whether the workspace still holds planning data; it then asks the
   * platform to archive and drops its own mirror row.
   */
  async archiveWorkspace(workspaceId: string): Promise<void> {
    const response = await this.createAuthenticatedFetch()(`${API_BASE}/roadmapper/workspaces/${encodeURIComponent(workspaceId)}`, { method: 'DELETE' });
    if (!response.ok) await this.handleResponse(response);
  }

  // Health check
  async healthCheck() {
    const response = await fetch(`${API_BASE}/health`);
    return response.text();
  }
}

export const api = new ApiClient();
